import React from 'react';
import Button from '@material-ui/core/Button';
import clsx from 'clsx';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import LinearProgress from '@material-ui/core/LinearProgress';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { Zoom } from 'react-toastify';
import { ActionDialog, Dialog, Input, showToast } from '@shared/components';
import { LoaderContext } from '@shared/contexts';
import { SharedService } from '@consumers/services';
import {
  BULK_UPLOAD_SECTIONS,
  TOAST_TYPES,
  bulkUploadModel as defaultBulkUploadModel,
} from '@shared/constants';
import {
  readFileAsBuffer,
  getSheetData,
  getFileContent,
  getFileExtension,
  mapFileContent,
  debounce,
  SUPPORTED_FILE_FORMATS,
} from '@utils';
import InfoModal from './info';
import Errors from './errors';
import config from '@config';
import DisabledExportIcon from '@assets/icons/export-disabled.svg';
import EnabledExportIcon from '@assets/icons/export.svg';
import SuccessIcon from '@assets/icons/done-green.svg';
import InfoIcon from '@assets/icons/info-light.svg';
import useStyles from './style';

const defaultState = {
  dataBeingUploaded: [],
  fileAlignment: 'Horizontal',
  fileContent: [],
  fileReadComplete: false,
  fileTabs: [],
  errors: {},
  headers: ['empty'],
  headerMap: {},
  infoInView: false,
  isOpen: false,
  overrideDuplicate: false,
  section: null,
  selectedFile: null,
  selectedTab: null,
  showErrorsModal: false,
  isOverrideErrorVisible: false,
  showGeocodeError: false,
  acknowledgedGeoCode: false,
  invokeUpload: false,
};

const duplicateCodes = ['SIT-043', 'SIT-045', 'DE-002', 'SU-025', 'CH-018'];
let hasSetupErrorOccurred = false;
export default ({
  accountId = null,
  accountUid = null,
  buttonProps = {},
  classes: passedClasses = {},
  disabled = false,
  section = null,
  templateUrl = null,
  uploadSections = null,
  uploadModel = null,
  helpers = {},
  isSiteCodeEnabled = false,
  onSuccess = () => {},
}) => {
  const { showLoader, hideLoader } = React.useContext(LoaderContext);
  const classes = useStyles();
  const [state, setState] = React.useState({
    ...defaultState,
    section: section ? section : defaultState.section,
  });

  const bulkUploadSections = React.useMemo(
    () => uploadSections || BULK_UPLOAD_SECTIONS,
    [uploadSections, BULK_UPLOAD_SECTIONS]
  );
  const bulkUploadModel = React.useMemo(
    () => uploadModel || defaultBulkUploadModel,
    [uploadModel, defaultBulkUploadModel]
  );

  const currentUploadModel = React.useMemo(
    () => bulkUploadModel[state.section],
    [state.section]
  );

  const getCurrentUploadModel = section => {
    if (section) {
      return bulkUploadSections.find(modal => modal.value === section);
    } else {
      return {
        label: 'data rows',
        value: null,
      };
    }
  };

  const downloadDataFile = () => {};

  const isUploadEnabled = () => {
    let isEnabled = false;
    // Enable upload if selected file is bulk upload template and user has not
    // selected any section as all data is can be uploaded by default.
    if (state.selectedFile && !state.section) {
      isEnabled = true;
    }

    // Verify whether all required fields have been mapped.
    if (state.section && state.selectedTab) {
      const requiredFields = currentUploadModel.filter(
        element => element.required
      );
      const mappedFields = (Object.keys(state.headerMap) || []).filter(
        key => !!state.headerMap[key]
      );
      // Validate each element only if number of mapped fields is at least
      // number of required elements in current section.
      if (mappedFields.length >= requiredFields.length) {
        isEnabled = true;
        requiredFields.forEach(field => {
          if (isEnabled) {
            if (!state.headerMap[field.dbFieldName]) {
              isEnabled = false;
            }
          }
        });
      }
    }

    return isEnabled;
  };

  /**
   * Map data and set values based on headers map of excel file.
   * @param {Array<Object>} currentUploadModel
   * @param {Array<Object>} dataBeingUploaded
   * @param {Object} headerMap
   */
  const prepareData = (
    currentUploadModel,
    dataBeingUploaded = [],
    headerMap
  ) => {
    const mappedData = [];
    dataBeingUploaded.forEach(dataRow => {
      const mappedDataRow = {};
      currentUploadModel.forEach(element => {
        if (
          Object.prototype.hasOwnProperty.call(dataRow, element.dbFieldName)
        ) {
          mappedDataRow[element.dbFieldName] = dataRow[element.dbFieldName];
        } else {
          mappedDataRow[element.dbFieldName] =
            dataRow[headerMap[element.dbFieldName]] || null;
        }

        if (
          !headerMap[element.dbFieldName] &&
          mappedDataRow[element.dbFieldName]
        ) {
          mappedDataRow[element.dbFieldName] = null;
        }
      });
      mappedData.push(mappedDataRow);
    });

    return mappedData;
  };

  const prepareBatches = data => {
    let batches = [];
    for (
      let batchIndex = 1;
      batchIndex <= Math.ceil(data.length / config.bulkUploadChunkSize);
      batchIndex += 1
    ) {
      batches.push(
        (
          data.slice(
            (batchIndex - 1) * config.bulkUploadChunkSize,
            batchIndex * config.bulkUploadChunkSize
          ) || []
        ).map(row => ({ ...row, batchIndex }))
      );
    }

    return batches;
  };

  const uploadBatches = async (
    batches,
    section,
    overrideDuplicate = state.overrideDuplicate
  ) => {
    return new Promise(async resolve => {
      const responses = [];
      if (batches && batches.length === 0) {
        resolve(responses);
      }
      // for await (let batch of batches) {
      for (let index = 0; index < batches.length; index += 1) {
        const response = await SharedService.bulkUpload(
          batches[index],
          section,
          accountId,
          typeof overrideDuplicate === 'boolean' ? overrideDuplicate : false
        );

        console.log('RESPONSE', response);
        responses.push(response);

        if (index === batches.length - 1) {
          resolve(responses);
        }
      }
    });
  };

  // Handlers
  const handleDialogClose = React.useCallback(() => {
    setState(defaultState);
  }, []);

  const handleDownloadClick = React.useCallback(() => {
    window.open(templateUrl || config.bulkUploadTemplateUrl, '_blank');
  }, []);

  const resetContentState = React.useCallback(() => {
    setState(prevState => ({
      ...prevState,
      dataBeingUploaded: defaultState.dataBeingUploaded,
      errors: defaultState.errors,
      fileAlignment: defaultState.fileAlignment,
      fileContent: defaultState.fileContent,
      fileReadComplete: defaultState.fileReadComplete,
      fileTabs: defaultState.fileTabs,
      headerMap: defaultState.headerMap,
      section: section ? section : defaultState.section,
      selectedTab: defaultState.selectedTab,
      acknowledgedGeoCode: defaultState.acknowledgedGeoCode,
      showGeocodeError: defaultState.showGeocodeError,
      showErrorsModal: defaultState.showErrorsModal,
      isOverrideErrorVisible: defaultState.isOverrideErrorVisible,
      overrideDuplicate: defaultState.overrideDuplicate,
    }));
  }, []);

  const handleFileSelection = React.useCallback(async event => {
    let selectedFile = null;
    if (event.currentTarget.files && event.currentTarget.files.length > 0) {
      selectedFile = event.currentTarget.files[0];
    }

    if (!selectedFile) {
      return;
    }

    // Reset state data that is dependent on selected file.
    resetContentState();
    // Verify selected file type is supported.
    const fileExtension = getFileExtension(selectedFile.name);
    if (fileExtension) {
      if (SUPPORTED_FILE_FORMATS.BULK_UPLOAD.includes(fileExtension)) {
        // Preserve selected file for future operations if file type
        // is supported.
        setState(prevState => ({
          ...prevState,
          selectedFile,
          fileReadComplete: false,
        }));

        // Read file content and verify data existence.
        const fileContent = await getFileContent(selectedFile, { header: 1 });
        if (!fileContent || Object.keys(fileContent).length === 0) {
          showToast('Selected file is empty.', TOAST_TYPES.ERROR);
          return;
        }

        // Verify selected file is bulk upload template if file is
        // an Excel file.
        let fileTabs = defaultState.fileTabs;

        if (fileExtension !== 'csv') {
          fileTabs = fileContent.SheetNames;
        }

        setState(prevState => ({
          ...prevState,
          fileAlignment: prevState.fileAlignment
            ? prevState.fileAlignment
            : null,
          fileContent,
          fileReadComplete: true,
          fileTabs,
        }));
      } else {
        showToast(
          `Only ${SUPPORTED_FILE_FORMATS.BULK_UPLOAD.join(
            ', '
          )} formats are supported.`,
          TOAST_TYPES.ERROR
        );
      }
    }
  }, []);

  const handleUploadClick = async (
    overrideDuplicate = state.overrideDuplicate,
    explicitSection = null
  ) => {
    if (!state.selectedFile) {
      return;
    }

    hasSetupErrorOccurred = false;
    showLoader();
    const section =
      typeof explicitSection === 'string' && explicitSection
        ? explicitSection
        : state.section;

    if (!section) {
      let errors = {};
      let uploadData = {};
      for await (let section of bulkUploadSections) {
        if (state.showGeocodeError || hasSetupErrorOccurred) {
          hideLoader();
          return;
        }
        showToast(
          `Validating ${section.label} records, please wait..`,
          TOAST_TYPES.INFO,
          { autoClose: 3000, transition: Zoom }
        );
        const dataBeingUploaded =
          state.dataBeingUploaded[section.value] ||
          mapFileContent(
            getSheetData(state.fileContent, section.sheetName),
            state.fileAlignment,
            section.value
          );

        if (!dataBeingUploaded || dataBeingUploaded.length === 0) {
          continue;
        }

        let currentUploadModel = bulkUploadModel[section.value];
        const excelHeaders = dataBeingUploaded[0]
          ? Object.keys(dataBeingUploaded[0])
          : [];

        const headerMap = mapHeaders(section.value, excelHeaders);
        const validationResult = await helpers.validate(
          prepareData(currentUploadModel, dataBeingUploaded, headerMap),
          currentUploadModel,
          section.value,
          accountId,
          accountUid,
          isSiteCodeEnabled
        );

        if (validationResult.setupError) {
          hasSetupErrorOccurred = true;
          hideLoader();
          return;
        }
        if (validationResult.hasGeocodeError && !state.acknowledgedGeoCode) {
          setState(prevState => ({
            ...prevState,
            dataBeingUploaded: {
              ...prevState.dataBeingUploaded,
              [section.value]: validationResult.updatedData,
            },
            errors: {
              ...prevState.errors,
              [section.value]: validationResult.errors,
            },
            showGeocodeError: true,
            invokeUpload: false,
          }));
          hideLoader();
          return;
        }
        errors[section.value] = validationResult.errors;
        console.log('errors', errors);
        uploadData[section.value] = validationResult.updatedData.slice();
      }

      const errorSections = Object.keys(errors);
      let hasError = false;
      errorSections.forEach(errorSection => {
        if (hasError) {
          return;
        } else {
          hasError = !!errors[errorSection].find(
            errorRow => Object.keys(errorRow).length > 0
          );
        }
      });

      if (hasError) {
        setState(prevState => ({
          ...prevState,
          dataBeingUploaded: uploadData,
          errors,
          showErrorsModal: true,
          invokeUpload: false,
        }));
      } else {
        let hasOverrideError = false;
        for await (let section of bulkUploadSections) {
          if (hasOverrideError) {
            return;
          }

          if (
            !uploadData[section.value] ||
            uploadData[section.value].length === 0
          ) {
            continue;
          }

          const batches = prepareBatches(uploadData[section.value]);
          showToast(
            `Uploading ${section.label} records, please wait..`,
            TOAST_TYPES.INFO,
            { autoClose: 2000, transion: Zoom }
          );
          const responses = await uploadBatches(
            batches,
            section.value,
            overrideDuplicate
          );
          let explicitError = null;
          let removedBatchesCount = 0;
          responses.forEach((response, responseIndex) => {
            const { data, error } = response;
            if (error) {
              if (Array.isArray(error)) {
                if (
                  !hasOverrideError &&
                  duplicateCodes.includes(error[0].code)
                ) {
                  hasOverrideError = true;
                }
              } else {
                if (!explicitError) {
                  explicitError = error;
                }
              }
            } else {
              uploadData[section.value].splice(
                (batches[responseIndex][0].batchIndex -
                  1 -
                  removedBatchesCount) *
                  config.bulkUploadChunkSize,
                config.bulkUploadChunkSize
              );
              removedBatchesCount += 1;
            }
          });

          if (explicitError) {
            showToast(
              explicitError.message || explicitError,
              TOAST_TYPES.ERROR
            );
            setState(prevState => ({
              ...prevState,
              dataBeingUploaded: {
                ...prevState.dataBeingUploaded,
                [section.value]: uploadData[section.value],
              },
              invokeUpload: false,
            }));
            hideLoader();
            return;
          }

          if (hasOverrideError) {
            setState(prevState => ({
              ...prevState,
              dataBeingUploaded: {
                ...prevState.dataBeingUploaded,
                [section.value]: uploadData[section.value],
              },
              isOverrideErrorVisible: true,
              invokeUpload: false,
            }));

            hideLoader();
            return;
          }

          const responseErrors = [];
          responses.forEach((response, responseIndex) => {
            const { data, error } = response;
            if (error) {
              if (Array.isArray(error)) {
                responseErrors.push(...error);
              }
            }
          });

          errors[section.value] = responseErrors;
        }

        setState(prevState => ({
          ...prevState,
          dataBeingUploaded: uploadData,
          errors,
          showErrorsModal: !!Object.keys(errors).find(
            sectionKey => Object.keys(errors[sectionKey]).length > 0
          ),
          invokeUpload: false,
        }));

        if (
          !Object.keys(errors).find(
            sectionKey => Object.keys(errors[sectionKey]).length > 0
          )
        ) {
          onSuccess();
          showToast('Uploaded successfully.', TOAST_TYPES.SUCCESS);
          setState(defaultState);
        }
      }

      hideLoader();
      return;
    }

    // debugger;

    const validationResult = await helpers.validate(
      prepareData(
        currentUploadModel,
        state.dataBeingUploaded[section],
        state.headerMap
      ),
      currentUploadModel,
      section,
      accountId,
      accountUid,
      isSiteCodeEnabled
    );

    if (validationResult?.explicitErrors?.length) {
      validationResult.explicitErrors.map(error => {
        showToast(error, TOAST_TYPES.ERROR);
      });
      return hideLoader();
    }
    if (validationResult.setupError) {
      hasSetupErrorOccurred = true;
      hideLoader();
      return;
    }
    if (validationResult.hasGeocodeError && !state.acknowledgedGeoCode) {
      setState(prevState => ({
        ...prevState,
        dataBeingUploaded: {
          ...prevState.dataBeingUploaded,
          [section]: validationResult.updatedData,
        },
        errors: {
          ...prevState.errors,
          [section]: validationResult.errors,
        },
        showGeocodeError: true,
        invokeUpload: false,
      }));

      hideLoader();
      return;
    }

    setState(prevState => ({
      ...prevState,
      errors: { [section]: validationResult.errors },
      dataBeingUploaded: {
        ...prevState.dataBeingUploaded,
        [section]: validationResult.updatedData.slice(),
      },
      invokeUpload: false,
    }));

    if (!validationResult.errors.find(error => Object.keys(error).length > 0)) {
      const batches = prepareBatches(validationResult.updatedData);
      const responses = await uploadBatches(
        batches,
        state.section,
        overrideDuplicate
      );

      let hasOverrideError = false;
      let explicitError = null;
      let removedBatchesCount = 0;
      responses.forEach((response, responseIndex) => {
        const { data, error } = response;
        if (error) {
          if (Array.isArray(error)) {
            if (!hasOverrideError && duplicateCodes.includes(error[0].code)) {
              hasOverrideError = true;
            }
          } else {
            if (!explicitError) {
              explicitError = error;
            }
          }
        } else {
          validationResult.updatedData.splice(
            (batches[responseIndex][0].batchIndex - 1 - removedBatchesCount) *
              config.bulkUploadChunkSize,
            config.bulkUploadChunkSize
          );
          removedBatchesCount += 1;
        }
      });

      if (explicitError) {
        showToast(explicitError.message || explicitError, TOAST_TYPES.ERROR);
        setState(prevState => ({
          ...prevState,
          dataBeingUploaded: {
            ...prevState.dataBeingUploaded,
            [section]: validationResult.updatedData,
          },
          invokeUpload: false,
        }));
        hideLoader();
        return;
      }

      if (hasOverrideError) {
        setState(prevState => ({
          ...prevState,
          dataBeingUploaded: {
            ...prevState.dataBeingUploaded,
            [section]: validationResult.updatedData,
          },
          isOverrideErrorVisible: true,
          invokeUpload: false,
        }));

        hideLoader();
        return;
      }

      let errors = [];
      responses.forEach((response, responseIndex) => {
        const { data, error } = response;
        if (error) {
          if (Array.isArray(error)) {
            errors = [...errors, ...error];
          }
        }
      });

      if (errors.length === 0) {
        onSuccess();
        showToast('Uploaded successfully.', TOAST_TYPES.SUCCESS);
        setState(defaultState);
      } else {
        setState(prevState => ({
          ...prevState,
          errors: { ...prevState.errors, [section]: errors },
          //   dataBeingUploaded: validationResult.updatedData,
          dataBeingUploaded: {
            ...prevState.dataBeingUploaded,
            [section]: validationResult.updatedData,
          },
          showErrorsModal: errors.length > 0,
          invokeUpload: false,
        }));
      }
    } else {
      setState(prevState => ({
        ...prevState,
        errors: {
          ...prevState.errors,
          [section]: validationResult.errors,
        },
        showErrorsModal: true,
        invokeUpload: false,
      }));
    }

    hideLoader();
  };

  const sectionOptions = React.useMemo(() => {
    const options = bulkUploadSections.map(section => ({
      ...section,
      isSelected: state.section === section.value,
    }));
    options.unshift({
      label: 'All',
      value: null,
      isSelected: !state.section,
    });

    return options;
  }, [state.section]);

  const mapHeaders = React.useCallback(
    (section, headers = []) => {
      const uploadModel = bulkUploadModel[section];
      const headerMap = { ...defaultState.headerMap };

      uploadModel.forEach(element => {
        if (headers.includes(element.field)) {
          headerMap[element.dbFieldName] = element.field;
        }
        if (headers.includes(element.dbFieldName)) {
          headerMap[element.dbFieldName] = element.dbFieldName;
        }
      });

      return headerMap;
    },
    [state.section]
  );

  // Lifecycle effects
  React.useEffect(() => {
    if (state.selectedTab && state.fileAlignment) {
      const excelHeaders =
        state.dataBeingUploaded[state.section] &&
        state.dataBeingUploaded[state.section][0]
          ? Object.keys(state.dataBeingUploaded[state.section][0]).filter(
              header => header !== 'null'
            )
          : [];

      let headerMap = mapHeaders(state.section, excelHeaders);

      setState(prevState => ({
        ...prevState,
        headers: excelHeaders,
        headerMap,
      }));
    }
  }, [state.selectedTab, state.fileAlignment]);

  React.useEffect(() => {
    if (!state.showGeocodeError && state.acknowledgedGeoCode) {
      handleUploadClick();
    }
  }, [state.showGeocodeError, state.acknowledgedGeoCode]);

  React.useEffect(() => {
    if (state.invokeUpload) {
      handleUploadClick();
    }
  }, [state.invokeUpload]);

  const onErrorsChange = React.useCallback(
    async (
      dataBeingUploaded,
      errors,
      sectionModel,
      field,
      section,
      updatedRow
    ) => {
      if (helpers.onChange) {
        showLoader();
        await helpers.onChange(
          sectionModel,
          dataBeingUploaded[section],
          field,
          updatedRow,
          accountId,
          accountUid
        );
        hideLoader();
      }

      setState(prevState => ({
        ...prevState,
        dataBeingUploaded,
        errors,
      }));
    },
    []
  );

  const onErrorsCancel = React.useCallback(() => {
    setState(prevState => ({
      ...prevState,
      showErrorsModal: false,
      errors: {},
    }));
  }, []);

  return (
    <>
      <Button
        disabled={disabled}
        startIcon={
          <img
            className='inverted'
            src={disabled ? DisabledExportIcon : EnabledExportIcon}
          />
        }
        variant='text'
        {...buttonProps}
        className={classes.button}
        onClick={event => {
          setState(prevState => ({
            ...prevState,
            isOpen: true,
          }));
        }}
      >
        Bulk Upload
      </Button>
      <Dialog
        classes={{
          paper: `${classes.dialog} ${passedClasses.dialog}`,
        }}
        maxWidth='md'
        open={state.isOpen}
        onClose={handleDialogClose}
      >
        <Dialog.Title hasClose>
          {state.infoInView ? 'Learn about bulk uploading' : 'Bulk Upload'}
        </Dialog.Title>
        <Dialog.Content>
          {state.infoInView ? (
            <InfoModal />
          ) : (
            <>
              <Typography variant='body2'>
                Here you can upload lots of data quickly to help build your
                account. To get started, download the Conductor bulk upload
                template or select the file you would like to upload.
              </Typography>
              <div className='mt-2'>
                <Typography
                  className='c-pointer color-text-link'
                  variant='body2'
                  component='span'
                  onClick={() => {
                    setState(prevState => ({
                      ...prevState,
                      infoInView: true,
                    }));
                  }}
                >
                  Learn more
                </Typography>
                <Typography className='ml-1' variant='body2' component='span'>
                  about bulk uploading in Conductor.
                </Typography>
              </div>
              <div className='mt-5 d-flex'>
                <input
                  className='d-none'
                  id='file-input-field'
                  type='file'
                  onChange={handleFileSelection}
                />
                <Button
                  color='primary'
                  component='label'
                  htmlFor='file-input-field'
                  variant='contained'
                >
                  Choose File
                </Button>
                <Button
                  className='ml-6'
                  color='primary'
                  onClick={handleDownloadClick}
                  startIcon={<img src={EnabledExportIcon} />}
                  variant='outlined'
                >
                  Download Template
                </Button>
              </div>
              {state.selectedFile && (
                <>
                  <div className='mw-50 mt-4 d-flex f-align-center'>
                    <Typography
                      className='color-text-secondary mr-3'
                      variant='caption'
                      noWrap
                    >
                      {state.selectedFile.name}
                    </Typography>
                    <LinearProgress
                      className='w-100'
                      variant={
                        state.fileReadComplete ? 'determinate' : 'indeterminate'
                      }
                      value={state.fileReadComplete ? 100 : null}
                    />
                    {state.fileReadComplete && (
                      <img className='ml-3' src={SuccessIcon} />
                    )}
                  </div>
                  <div className='d-flex f-justify-start f-align-center w-75'>
                    {state.fileReadComplete && (
                      <div className='w-50 mr-5'>
                        <Input
                          fullWidth
                          disabled={!!section}
                          label='Data to upload'
                          options={sectionOptions}
                          onChange={selectedOption => {
                            let dataBeingUploaded = state.dataBeingUploaded;
                            // debugger;
                            if (state.fileTabs.length === 0) {
                              dataBeingUploaded = {
                                [selectedOption.value]: mapFileContent(
                                  state.fileContent,
                                  state.fileAlignment,
                                  selectedOption.value
                                ),
                              };
                            }

                            setState(prevState => ({
                              ...prevState,
                              dataBeingUploaded,
                              section: selectedOption.value,
                            }));
                          }}
                        />
                      </div>
                    )}
                    {state.section && state.fileTabs.length > 0 && (
                      <div className='w-50'>
                        <Input
                          fullWidth
                          label='Spreadsheet tab to upload'
                          options={state.fileTabs.map(tab => ({
                            label: tab,
                            isSelected: tab === state.selectedTab,
                          }))}
                          onChange={async selectedOption => {
                            let dataBeingUploaded = state.dataBeingUploaded;
                            if (state.fileAlignment) {
                              const sheetValues = getSheetData(
                                state.fileContent,
                                selectedOption.label
                              );

                              dataBeingUploaded = {
                                // ...dataBeingUploaded,
                                [state.section]: mapFileContent(
                                  sheetValues,
                                  state.fileAlignment,
                                  state.section
                                ),
                              };
                              // debugger;
                            }

                            setState(prevState => ({
                              ...prevState,
                              selectedTab: selectedOption.label,
                              dataBeingUploaded,
                              headerMap: defaultState.headerMap,
                            }));
                          }}
                        />
                      </div>
                    )}
                  </div>
                  {state.section &&
                    (state.fileTabs.length === 0 || state.selectedTab) && (
                      <div className='mt-4 d-flex f-align-center'>
                        <Typography className='mr-4' variant='body2'>
                          Spread Sheet Format
                        </Typography>
                        <RadioGroup
                          className='d-flex flex-row'
                          value={state.fileAlignment}
                          onChange={async event => {
                            const fileAlignment = event.currentTarget.value;
                            let dataBeingUploaded = state.dataBeingUploaded;
                            if (state.fileTabs.length > 0) {
                              dataBeingUploaded = {
                                ...dataBeingUploaded,
                                [state.section]: mapFileContent(
                                  getSheetData(
                                    state.fileContent,
                                    state.selectedTab
                                  ),
                                  fileAlignment,
                                  state.section
                                ),
                              };
                            } else {
                              dataBeingUploaded = {
                                ...dataBeingUploaded,
                                [state.section]: mapFileContent(
                                  state.fileContent,
                                  fileAlignment,
                                  state.section
                                ),
                              };
                            }

                            setState(prevState => ({
                              ...prevState,
                              headerMap: defaultState.headerMap,
                              fileAlignment,
                              dataBeingUploaded,
                            }));
                          }}
                        >
                          <FormControlLabel
                            className='mr-6'
                            control={<Radio />}
                            value='Horizontal'
                            label='Horizontal'
                          />
                          <FormControlLabel
                            control={<Radio />}
                            value='Vertical'
                            label='Vertical'
                          />
                        </RadioGroup>
                      </div>
                    )}
                  {(state.fileTabs.length === 0 || state.selectedTab) &&
                    state.fileAlignment &&
                    state.dataBeingUploaded[state.section] &&
                    state.dataBeingUploaded[state.section].length > 0 && (
                      <div className='mt-8'>
                        <Typography variant='h5'>
                          Create mappings for each desired section.
                        </Typography>
                        {currentUploadModel.map(element => {
                          return (
                            <div className='d-flex f-align-center'>
                              <div
                                className='w-25'
                                title={
                                  state.headerMap[element.dbFieldName] ||
                                  'empty'
                                }
                              >
                                <Input
                                  fullWidth
                                  className={clsx({
                                    [classes.mappedField]:
                                      !!state.headerMap[element.dbFieldName],
                                  })}
                                  options={['empty', ...state.headers].map(
                                    option => ({
                                      label: option,
                                      value: option === 'empty' ? null : option,
                                      isSelected:
                                        state.headerMap[element.dbFieldName] ===
                                          option ||
                                        (option === 'empty' &&
                                          !state.headerMap[
                                            element.dbFieldName
                                          ]),
                                    })
                                  )}
                                  onChange={selectedOption => {
                                    const headerMap = { ...state.headerMap };
                                    headerMap[element.dbFieldName] =
                                      selectedOption.value;
                                    setState(prevState => ({
                                      ...prevState,
                                      headerMap,
                                    }));
                                  }}
                                  placeholder={element.field}
                                />
                              </div>
                              <div className='w-25 mr-2'>
                                {!!state.headerMap[element.dbFieldName] && (
                                  <div className={classes.mappedFieldWrapper}>
                                    <span></span>
                                  </div>
                                )}
                              </div>
                              <div className='w-50 d-flex f-align-center'>
                                <Tooltip
                                  title={`${element.field} (${element.type})`}
                                  placement='top-start'
                                >
                                  <Typography variant='body2'>
                                    {`${element.field} (${element.type}) `}
                                    {element.required && (
                                      <span className='color-error'>*</span>
                                    )}
                                  </Typography>
                                </Tooltip>
                                <span className='ml-4'>
                                  <Tooltip title={element.example}>
                                    <img src={InfoIcon} className='info-icon' />
                                  </Tooltip>
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                </>
              )}
            </>
          )}
        </Dialog.Content>
        <Dialog.Actions>
          {state.infoInView ? (
            <Button
              color='primary'
              onClick={() => {
                setState(prevState => ({
                  ...prevState,
                  infoInView: false,
                }));
              }}
              variant='contained'
            >
              Back
            </Button>
          ) : (
            <>
              <Button onClick={handleDialogClose}>Cancel</Button>
              <Button
                color='primary'
                disabled={!isUploadEnabled()}
                onClick={handleUploadClick}
                variant='contained'
              >
                Upload
              </Button>
            </>
          )}
        </Dialog.Actions>
        <Errors
          helpers={helpers}
          uploadModel={bulkUploadModel}
          uploadSections={bulkUploadSections}
          //   onChange={onErrorsChange}
          onUploadClick={(dataBeingUploaded, errors) => {
            setState(prevState => ({
              ...prevState,
              dataBeingUploaded,
              errors,
              invokeUpload: true,
            }));
          }}
          open={state.showErrorsModal}
          dataBeingUploaded={state.dataBeingUploaded}
          errors={state.errors}
          onCancel={onErrorsCancel}
          accountId={accountId}
          accountUid={accountUid}
        />
      </Dialog>
      <ActionDialog
        classes={{
          confirm: 'bg-danger color-white',
        }}
        open={state.isOverrideErrorVisible}
        contentText={`One or more ${
          getCurrentUploadModel(state.section).label
        } already exists. Do you want to override existing data?`}
        isConfirmation
        positiveActionLabel='Continue'
        negativeActionLabel='Abort'
        onConfirm={() => {
          setState(prevState => ({
            ...prevState,
            isOverrideErrorVisible: false,
            overrideDuplicate: true,
          }));

          handleUploadClick(true);
        }}
        onCancel={() => {
          setState(prevState => ({
            ...prevState,
            isOverrideErrorVisible: false,
          }));
          downloadDataFile();
        }}
      />
      <ActionDialog
        heading='Address Not Geocoded'
        isConfirmation
        open={state.showGeocodeError}
        content={
          <Typography variant='body2'>
            Some addresses you entered are not Geocoded. If you are 100% certain
            these addresses are correct, you may proceed but you must{' '}
            <a
              href='https://bandwidth.com'
              target='_blank'
              className='color-text-link'
              rel='noopener noreferrer'
            >
              file a ticket with Bandwidth.com
            </a>{' '}
            to have addresses added to the Geocoding system.
          </Typography>
        }
        positiveActionLabel='Acknowledge'
        onConfirm={() => {
          setState(prevState => {
            return {
              ...prevState,
              showGeocodeError: false,
              acknowledgedGeoCode: true,
            };
          });
        }}
        onClose={() => {
          setState(prevState => ({
            ...prevState,
            showGeocodeError: false,
          }));
          downloadDataFile();
        }}
      />
    </>
  );
};
